import { useRef, useState } from "react";
import { Camera, X, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { marketApi, CONDITIONS } from "@/lib/marketApi";

const MAX_PHOTOS = 10;
const MAX_BYTES = 1_500_000; // ~1.5MB after b64

async function fileToCompressedDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 1280;
        let w = img.width;
        let h = img.height;
        if (w > maxDim || h > maxDim) {
          const r = Math.min(maxDim / w, maxDim / h);
          w = Math.round(w * r);
          h = Math.round(h * r);
        }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        let q = 0.85;
        let dataUrl = canvas.toDataURL("image/jpeg", q);
        while (dataUrl.length > MAX_BYTES && q > 0.4) {
          q -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", q);
        }
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ListingComposer({ cats, onClose, onCreated, initial = null }) {
  const auth = useAuth();
  const isEdit = !!initial;
  const [form, setForm] = useState(() => ({
    title: initial?.title || "",
    description: initial?.description || "",
    category: initial?.category || "other",
    type: initial?.type || "product",
    price_usd: initial?.price_usd ?? 0,
    currency: initial?.currency || "USD",
    location: initial?.location || "",
    lat: initial?.lat ?? null,
    lng: initial?.lng ?? null,
    photos: initial?.photos || [],
    condition: initial?.condition || "used",
    contact: initial?.contact || "",
  }));
  const [busy, setBusy] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (form.photos.length + files.length > MAX_PHOTOS) {
      toast.error(`Máximo ${MAX_PHOTOS} fotos`);
      return;
    }
    setUploadingCount(files.length);
    try {
      const dataUrls = await Promise.all(files.map(fileToCompressedDataUrl));
      set("photos", [...form.photos, ...dataUrls]);
    } catch (ex) {
      toast.error("Error procesando imágenes");
    } finally {
      setUploadingCount(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (idx) => {
    set("photos", form.photos.filter((_, i) => i !== idx));
  };

  const useGps = () => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no soporta geolocalización");
      return;
    }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        set("lat", Number(pos.coords.latitude.toFixed(6)));
        set("lng", Number(pos.coords.longitude.toFixed(6)));
        setGeoBusy(false);
        toast.success("Ubicación capturada");
      },
      (err) => {
        setGeoBusy(false);
        toast.error(err.message || "No se pudo obtener ubicación");
      },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Título obligatorio");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, price_usd: Number(form.price_usd) || 0 };
      const data = isEdit
        ? await marketApi.update(auth.token, initial.id, payload)
        : await marketApi.create(auth.token, payload);
      toast.success(isEdit ? "Anuncio actualizado" : "Anuncio publicado");
      onCreated?.(data);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Error al publicar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-[80] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} data-testid="market-composer" className="bg-cream-card border hairline rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-auto p-5 md:p-6">
        <h3 className="font-serif text-2xl tracking-tight mb-4">
          {isEdit ? "Editar anuncio" : "Publicar anuncio"}
        </h3>

        <form onSubmit={submit} className="space-y-4">
          {/* Photos */}
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">
              Fotos · hasta {MAX_PHOTOS} (la primera es portada)
            </span>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {form.photos.map((src, i) => (
                <div key={i} data-testid={`composer-photo-${i}`} className="relative aspect-square rounded-xl overflow-hidden border hairline group">
                  <img src={src} alt={`foto ${i + 1}`} className="w-full h-full object-cover" />
                  {i === 0 && (
                    <span className="absolute top-1 left-1 bg-coral text-white text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-full">portada</span>
                  )}
                  <button type="button" onClick={() => removePhoto(i)} data-testid={`composer-photo-remove-${i}`}
                          className="absolute top-1 right-1 bg-ink text-cream rounded-full p-1 opacity-0 group-hover:opacity-100 transition">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {form.photos.length < MAX_PHOTOS && (
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="composer-add-photo"
                        className="aspect-square border-2 border-dashed hairline rounded-xl flex flex-col items-center justify-center text-ink-mid hover:border-coral hover:text-coral transition">
                  {uploadingCount > 0
                    ? <Loader2 className="w-5 h-5 animate-spin" />
                    : <Camera className="w-5 h-5" />}
                  <span className="text-[10px] font-mono uppercase mt-1">
                    {uploadingCount > 0 ? "Subiendo…" : "Añadir"}
                  </span>
                </button>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
          </div>

          <Field testid="composer-title" label="Título *" value={form.title} onChange={(v) => set("title", v)} required />
          <Area testid="composer-desc" label="Descripción" value={form.description} onChange={(v) => set("description", v)} />

          <div className="grid sm:grid-cols-2 gap-3">
            <Sel testid="composer-category" label="Categoría" value={form.category} onChange={(v) => set("category", v)}
                 options={cats.map((c) => ({ id: c.id, label: c.label }))} />
            <Sel testid="composer-type" label="Tipo" value={form.type} onChange={(v) => set("type", v)}
                 options={[{ id: "product", label: "Producto" }, { id: "service", label: "Servicio" }]} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field testid="composer-price" label="Precio USD" type="number" value={form.price_usd} onChange={(v) => set("price_usd", v)} />
            <Sel testid="composer-condition" label="Condición" value={form.condition} onChange={(v) => set("condition", v)}
                 options={CONDITIONS} />
          </div>

          {/* Location */}
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">Ubicación</span>
            <Field testid="composer-location" label="" value={form.location} onChange={(v) => set("location", v)} placeholder="ej. Ciudad de México · Polanco" />
            <div className="flex items-center justify-between mt-2 gap-2">
              <button type="button" onClick={useGps} disabled={geoBusy} data-testid="composer-gps"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border hairline hover:border-coral transition disabled:opacity-50">
                {geoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
                {form.lat ? "Actualizar GPS" : "Usar mi ubicación"}
              </button>
              {form.lat !== null && form.lng !== null && (
                <span className="text-[11px] font-mono text-ink-mid">
                  {form.lat.toFixed(4)}, {form.lng.toFixed(4)}
                  <button type="button" onClick={() => { set("lat", null); set("lng", null); }}
                          className="ml-2 text-red-700 hover:underline">limpiar</button>
                </span>
              )}
            </div>
          </div>

          <Field testid="composer-contact" label="Contacto opcional (WhatsApp, email)" value={form.contact} onChange={(v) => set("contact", v)} />

          {isEdit && (
            <Sel testid="composer-status" label="Estado del anuncio"
                 value={initial.status} onChange={(v) => set("status", v)}
                 options={[
                   { id: "active", label: "Activo" },
                   { id: "paused", label: "Pausado (oculto)" },
                   { id: "sold", label: "Vendido" },
                 ]} />
          )}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose}
                    className="flex-1 border hairline rounded-full py-2.5 text-sm hover:border-coral transition">
              Cancelar
            </button>
            <button type="submit" disabled={busy} data-testid="composer-submit"
                    className="flex-[2] bg-coral text-white rounded-full py-2.5 text-sm hover:bg-coral-hover disabled:opacity-50">
              {busy ? "Guardando…" : (isEdit ? "Guardar cambios" : "Publicar")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, ...rest }) {
  return (
    <label className="block">
      {label && <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">{label}</span>}
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...rest}
             className="w-full bg-cream border hairline rounded-xl px-3 py-2.5 text-sm outline-none focus:border-coral" />
    </label>
  );
}
function Area({ label, testid, value, onChange }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">{label}</span>
      <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="w-full bg-cream border hairline rounded-xl px-3 py-2.5 text-sm outline-none focus:border-coral resize-y" />
    </label>
  );
}
function Sel({ label, testid, value, onChange, options }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid mb-1.5 block">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
              className="w-full bg-cream border hairline rounded-xl px-3 py-2.5 text-sm outline-none focus:border-coral">
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </label>
  );
}
